import { createAction, createReducer, isAnyOf } from '@reduxjs/toolkit';
import { message } from 'common/services/message';

import api from 'common/api';
import { PaginationState } from 'common/components/Pagination';
import { AppState } from 'common/models/AppState';
import { Transaction, VerificationFlag, VersionFlag } from 'common/models/Transaction';
import i18n from 'common/services/i18n';
import { FormState } from 'features/form/ducks';

import { startListening } from '../../../../root/store/listener-middleware';

export interface FormsListState {
  items: FormsListItem[];
  pagination: PaginationState;
  searchQuery: string;
  loading: boolean;
}

export type FormsListItem = {
  createdAt: string;
  id: string;
  isOutdated: boolean;
  isVerified: boolean;
  updatedAt: string;
} & FormState;

export const selectHome = (state: AppState) => state.home;

export const selectFormsListItems = (state: AppState) => selectHome(state).formsList.items;

export const selectFormsListPagination = (state: AppState) => selectHome(state).formsList.pagination;

export const selectFormsSearchQuery = (state: AppState) => selectHome(state).formsList.searchQuery;

export const selectIsFormsListLoading = (state: AppState) => selectHome(state).formsList.loading;

export const loadFormsListAction = createAction<Record<string, unknown> | undefined>('@@Home/FormsList/LOAD');

export const setFormsListLoadingAction = createAction<boolean>('@@Home/FormsList/SET_IS_LOADING');

export const setFormsItemsAction = createAction<Transaction[]>('@@Home/FormsList/SET_ITEMS');

export const setFormsListSearchQueryAction = createAction<string>('@@Home/FormsList/SET_SEARCH_QUERY');

export const setFormsListPageNumberAction = createAction<number>('@@Home/FormsList/SET_PAGE_NUMBER');

export const setFormsListPageSizeAction = createAction<number>('@@Home/FormsList/SET_PAGE_SIZE');

export const setFormsListItemCountAction = createAction<number>('@@Home/FormsList/SET_ITEM_COUNT');

const initialFormsOverviewState: FormsListState = {
  items: [],
  pagination: { currentPage: 1, pageSize: 10, total: 0 },
  searchQuery: '',
  loading: false,
};

export default createReducer(initialFormsOverviewState, (builder) => {
  builder
    .addCase(setFormsItemsAction, (state, { payload }) => {
      state.items = payload.map((t: Transaction) => ({
        id: t._id,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
        ownerId: t.owner,
        ...t.entries[0].meta,
        isOutdated: t.meta.version === VersionFlag.OUTDATED,
        isVerified:
          t?.meta?.verification?.customer1?.sms === VerificationFlag.VERIFIED &&
          t?.meta?.verification?.customer1?.email === VerificationFlag.VERIFIED,
      }));
    })
    .addCase(setFormsListPageNumberAction, (state, { payload }) => {
      state.pagination.currentPage = payload;
    })
    .addCase(setFormsListPageSizeAction, (state, { payload }) => {
      state.pagination.currentPage = 1;
      state.pagination.pageSize = payload;
    })
    .addCase(setFormsListItemCountAction, (state, { payload }) => {
      state.pagination.total = payload;
    })
    .addCase(setFormsListSearchQueryAction, (state, { payload }) => {
      state.searchQuery = payload;
    })
    .addCase(setFormsListLoadingAction, (state, { payload }) => {
      state.loading = payload;
    });
});

// Listeners
startListening({
  actionCreator: loadFormsListAction,
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    listenerApi.dispatch(setFormsListLoadingAction(true));

    const state = listenerApi.getState();
    const pagination = selectFormsListPagination(state);
    const searchQuery = selectFormsSearchQuery(state);
    const { ok, data } = await api.transaction.find({
      $limit: pagination.pageSize,
      $skip: pagination.pageSize * (pagination.currentPage - 1),
      ...(searchQuery ? { $text: { $search: searchQuery } } : {}),
    });

    if (ok && data) {
      listenerApi.dispatch(setFormsItemsAction(data.data));
      listenerApi.dispatch(setFormsListItemCountAction(data.total));
    } else {
      const t = await i18n;
      message.error(t('formsManagement.failedLoad'));
    }

    listenerApi.dispatch(setFormsListLoadingAction(false));
  },
});

startListening({
  matcher: isAnyOf(setFormsListPageNumberAction, setFormsListPageSizeAction, setFormsListSearchQueryAction),
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    listenerApi.dispatch(loadFormsListAction());
  },
});
